import React from "react";
import { Link } from "react-router-dom";
import {
    FileCheck2,
    Compass,
    HardHat,
    Home as HomeIcon,
    Zap,
    CheckCircle2,
    ArrowRight,
} from "lucide-react";

const SERVICES = [
    {
        icon: FileCheck2,
        title: "Permit Processing",
        desc: "Full-service permit expediting across all jurisdictions. We manage submittals, plan-check corrections, and approvals on your behalf.",
        bullets: [
            "Residential, ADU, commercial & multifamily permits",
            "Jurisdiction liaison & correction responses",
            "Grading, demolition & building permits",
        ],
        img: "https://images.unsplash.com/photo-1769721209842-e46c60e7fbf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBidWlsZGluZyUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3NzcwNTQ2NzV8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        icon: Compass,
        title: "Architectural Plans",
        desc: "Detailed architectural design and construction documents that pass plan check the first time.",
        bullets: [
            "Site, floor, elevation & section plans",
            "Code analysis & Title 24 compliance",
            "3D renderings for client approval",
        ],
        img: "https://images.unsplash.com/photo-1754780960162-839cda44d736?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50c3xlbnwwfHx8fDE3NzcwNTQ2NzV8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        icon: HardHat,
        title: "Engineering Coordination",
        desc: "Licensed structural, civil, and MEP engineering coordination so you have one point of contact.",
        bullets: [
            "Structural calculations & details",
            "Civil grading & drainage",
            "Mechanical, electrical, plumbing design",
        ],
    },
    {
        icon: HomeIcon,
        title: "ADU & Residential Projects",
        desc: "From detached ADUs to whole-home remodels, we specialize in streamlining residential approvals.",
        bullets: [
            "Detached, attached & JADU designs",
            "Room additions & garage conversions",
            "Custom single-family homes",
        ],
    },
    {
        icon: Zap,
        title: "Utility Connection Coordination",
        desc: "We coordinate water, sewer, gas, and electrical hookups so your site is build-ready.",
        bullets: [
            "Will-serve letters & capacity studies",
            "Meter set and panel upgrade permits",
            "Public works encroachment permits",
        ],
    },
    {
        icon: CheckCircle2,
        title: "Expediting Services",
        desc: "Priority handling and direct jurisdiction relationships to unlock faster approvals.",
        bullets: [
            "Over-the-counter reviews when eligible",
            "Same-day submittals in major metros",
            "Escalation management for stalled permits",
        ],
    },
];

export default function Services() {
    return (
        <div data-testid="page-services" className="bg-white">
            {/* HERO */}
            <section className="bg-[#0A2540] text-white py-24 relative overflow-hidden" data-testid="services-hero">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">Services</div>
                    <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
                        Plans, permits, and engineering—<span className="text-[#C5A059]">all under one roof.</span>
                    </h1>
                    <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
                        We handle the entire pre-construction phase so you can focus on building. Every service is delivered by licensed professionals with deep jurisdiction expertise.
                    </p>
                </div>
            </section>

            {/* GRID */}
            <section className="py-24" data-testid="services-grid">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {SERVICES.map((s, idx) => (
                        <article
                            key={s.title}
                            className="group bg-white border border-[#E2E8F0] p-8 md:p-10 flex flex-col hover:border-[#C5A059] transition-colors"
                            data-testid={`service-card-${idx}`}
                        >
                            <div className="flex items-start justify-between gap-6">
                                <div className="w-14 h-14 flex items-center justify-center bg-[#0A2540] text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white transition-colors flex-shrink-0">
                                    <s.icon size={24} />
                                </div>
                                {s.img && (
                                    <img
                                        src={s.img}
                                        alt=""
                                        className="w-24 h-24 object-cover opacity-70"
                                    />
                                )}
                            </div>
                            <h3 className="mt-6 text-2xl font-semibold text-[#0A2540]">{s.title}</h3>
                            <p className="mt-3 text-[#475569] leading-relaxed">{s.desc}</p>
                            <ul className="mt-6 space-y-2.5">
                                {s.bullets.map((b) => (
                                    <li key={b} className="flex gap-3 text-sm text-[#0A2540]">
                                        <CheckCircle2 size={16} className="mt-0.5 text-[#C5A059] flex-shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="services-cta">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                        Not sure which service you need?
                    </h2>
                    <p className="mt-4 text-[#475569]">
                        Send us your project details and we'll build a custom scope and quote—no obligation.
                    </p>
                    <Link
                        to="/contact"
                        className="mt-8 inline-flex items-center gap-2 btn-gold px-7 py-3.5 text-sm font-semibold"
                        data-testid="services-cta-link"
                    >
                        Request a Consultation <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
